import Vue from 'vue'
import { S, G, M, A } from './types'
import { Getters, Mutations, Actions } from 'vuex'
import { ECG_DATA_LENGTH, ECG_SAVED_DATA_LENGTH } from '@/constants/ecg'

const _maxLength = ECG_DATA_LENGTH
const _largeLength = ECG_SAVED_DATA_LENGTH
const offset = 20

/* -------------------------------------------------------------------------- */
const state = (): S => ({
  type: 'load',
  savedData: [],
  loadData: [...Array(_maxLength + offset)].map(() => ({
    ecg: NaN,
    measurementTime: new Date()
  })),
  largeData: [...Array(_largeLength)].map(() => ({
    ecg: NaN,
    measurementTime: new Date()
  })),
  largeDataEnabled: true,
  savedPr: 0,
  loadPr: -1,
  firstIdx: 0,
  maxLength: _maxLength,
  offset,
  battery: -1
})

/* -------------------------------------------------------------------------- */

const getters: Getters<S, G> = {
  currentData: state => {
    if (state.type === 'load') {
      return state.loadData
    }
    return state.savedData
  },
  currentPr: state => {
    if (state.type === 'load') {
      return state.loadPr
    }
    return state.savedPr
  }
}

/* -------------------------------------------------------------------------- */

const mutations: Mutations<S, M> = {
  disableLargeData: state => {
    state.largeDataEnabled = false
  },
  changeType: (state, { type }) => {
    state.type = type
  },
  setSavedData: (state, { data }) => {
    state.savedData = data
  },
  pushLoadData: (state, { data }) => {
    const len = data.ecg.length
    state.firstIdx =
      (state.firstIdx - len + state.maxLength + state.offset) %
      (state.maxLength + state.offset)
    state.loadData.splice(0, len)
    state.loadData = state.loadData.concat(
      data.ecg.map((v: number) => ({
        ecg: v,
        measurementTime: data.measurementTime
      }))
    )
    for (let index = 0; index < offset; index++) {
      Vue.set(state.loadData, index, NaN)
    }

    if (state.largeDataEnabled) {
      const overflowSize = state.largeData.length + len - _largeLength
      if (overflowSize > 0) {
        state.largeData.splice(0, overflowSize)
      }
      state.largeData = state.largeData.concat(
        data.ecg.map((v: number) => ({
          ecg: v,
          measurementTime: data.measurementTime
        }))
      )
    }
  },
  setPr: (state, pr) => {
    state.loadPr = pr
  },
  reset: state => {
    state.loadData = [...Array(_maxLength + offset)].map(() => ({
      ecg: NaN,
      measurementTime: new Date()
    }))
    state.largeData = [...Array(_largeLength)].map(() => ({
      ecg: NaN,
      measurementTime: new Date()
    }))
    state.loadPr = -1
    state.firstIdx = 0
  },
  setSavedPr: (state, pr) => {
    state.savedPr = pr
  },
  setBattery: (state, battery) => {
    state.battery = battery
  },
  initBattery: state => {
    state.battery = -1
  }
}

/* -------------------------------------------------------------------------- */

const actions: Actions<S, A, G, M> = {
  async changeType({ commit }, { type }) {
    commit('changeType', { type })
  },
  async save({ state }, { patientId }) {
    const _data = state.largeDataEnabled ? state.largeData : state.loadData
    const data = _data.filter(v => !isNaN(v.ecg))
    await Vue.$api.ecg.save({
      patientId,
      measureDatetime: data[0].measurementTime,
      heartWave: data.map(v => v.ecg),
      heartRate: state.loadPr
    })
  }
}

/* -------------------------------------------------------------------------- */

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}
